import {
  CLIENT_DOCUMENT_TYPES,
  formatClientDocument,
  isValidClientDocument,
  isValidPhone,
  normalizeCpf,
} from "./validationHelper";
import { isMaskedPhone } from "./phoneHelper";
import { buildGlobalSearchPayload } from "./globalSearchHelper";
import { buildAdvancedSearchPayload } from "./advancedSearchHelper";
import { TAX_ID_DOCUMENT_CATEGORY, toUploadFile } from "./taxIdChangeHelper";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

const COORDINATOR_DASHBOARD_PATH =
  process.env.REACT_APP_COORDINATOR_DASHBOARD_PATH ||
  "/cocos";
const COORDINATOR_COCO_DETAILS_PATH =
  process.env.REACT_APP_COORDINATOR_COCO_DETAILS_PATH ||
  "/cocos";
const COORDINATOR_CLIENT_DETAILS_PATH =
  process.env.REACT_APP_COORDINATOR_CLIENT_DETAILS_PATH ||
  "/cocos";

const fetchWithRetry = async (url, options, retries = 1) => {
  try {
    const response = await fetch(url, options);

    if (response.status === 504 && retries > 0) {
      console.warn(`Received 504, retrying... (${retries} retries left)`);
      return fetchWithRetry(url, options, retries - 1);
    }

    return response;
  } catch (err) {
    if (err?.name === "AbortError") {
      throw err;
    }
    // Retry only for network-level errors, not HTTP
    if (retries > 0) {
      console.warn(`Fetch failed, retrying... (${retries} retries left)`);
      return fetchWithRetry(url, options, retries - 1);
    }
    throw err;
  }
};

export const fetchFeatures = async (token, signal) => {
  const response = await fetch(`${API_BASE_URL}/features`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch features (${response.status})`);
  }

  return response.json();
};

// Pre-auth flags for the sign-in page (no token). Same keys as GET /features, PROD-safe subset only.
export const PUBLIC_FEATURES_PATH = "/features/public";

export const fetchPublicFeatures = async (signal) => {
  const response = await fetch(`${API_BASE_URL}${PUBLIC_FEATURES_PATH}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch public features (${response.status})`);
  }

  return response.json();
};

export const fetchNotifications = async (token, language, signal) => {
  const query = new URLSearchParams({ lang: language === "en" ? "en" : "pt" });
  const response = await fetch(`${API_BASE_URL}/notifications?${query}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch notifications (${response.status})`);
  }

  return response.json();
};

const postNotificationIds = async (action, token, ids) => {
  const response = await fetch(`${API_BASE_URL}/notifications/${action}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ ids }),
  });

  if (!response.ok) {
    throw new Error(`Failed to ${action} notifications (${response.status})`);
  }
};

export const markNotificationsRead = (token, ids) => postNotificationIds("read", token, ids);

export const dismissNotifications = (token, ids) => postNotificationIds("dismiss", token, ids);

export const MATTER_SCOPES = Object.freeze({
  ACTIVE: "ACTIVE",
  TERMINATED: "TERMINATED",
  BUSINESS: "BUSINESS",
});

// `matterScope` is only sent for non-default scopes so the production
// (ACTIVE) request URL is unchanged.
export const fetchClientPage = async (token, parameters = {}, signal) => {
  try {
    const { matterScope, ...rest } = parameters;
    const query = new URLSearchParams(
      Object.entries({
        ...rest,
        ...(matterScope && matterScope !== MATTER_SCOPES.ACTIVE ? { matterScope } : {}),
      }).filter(([, value]) => value !== null && value !== undefined && value !== ""),
    ).toString();
    const response = await fetchWithRetry(
      `${API_BASE_URL}/clients${query ? `?${query}` : ""}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        signal,
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error("Failed to fetch client page:", error);
    throw error;
  }
};

// Backward-compatible export for consumers that only need the first page.
export const fetchClientData = (token) => fetchClientPage(token);

export const fetchDiscontinuanceClientData = async (token) => {
  const response = await fetchWithRetry(
    `${API_BASE_URL}/clients/discontinuance`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    const message = await response.text();
    throw new Error(
      `Failed to fetch discontinuance clients (${response.status}): ${message}`,
    );
  }

  return response.json();
};

// `caseTab` is only sent when the tab-scoped summary feature is enabled;
// the production portfolio-wide request carries no query string.
export const fetchClientsSummary = async (token, caseTab, signal) => {
  const query = caseTab ? `?${new URLSearchParams({ caseTab })}` : "";
  const response = await fetchWithRetry(
    `${API_BASE_URL}/clients/summary${query}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      signal,
    },
  );

  if (!response.ok) {
    const message = await response.text();
    throw new Error(
      `Failed to fetch clients summary (${response.status}): ${message}`,
    );
  }

  return response.json();
};

export const fetchCoordinatorDashboard = async (
  token,
  continuationToken = null,
  searchTerm = "",
) => {
  const parameters = new URLSearchParams();
  if (continuationToken) parameters.set("continuationToken", continuationToken);
  if (searchTerm.trim()) parameters.set("searchTerm", searchTerm.trim());
  const queryString = parameters.toString();
  const query = queryString ? `?${queryString}` : "";
  const response = await fetchWithRetry(
    `${API_BASE_URL}${COORDINATOR_DASHBOARD_PATH}${query}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    const message = await response.text();
    throw new Error(
      `Failed to fetch coordinator dashboard (${response.status}): ${message}`,
    );
  }

  return response.json();
};

export const fetchCoordinatorCoCoDetails = async (cocoId, token, parameters = {}) => {
  const query = new URLSearchParams(
    Object.entries(parameters).filter(([, value]) => value !== null && value !== undefined && value !== ""),
  ).toString();
  const response = await fetchWithRetry(
    `${API_BASE_URL}${COORDINATOR_COCO_DETAILS_PATH}/${encodeURIComponent(cocoId)}${
      query ? `?${query}` : ""
    }`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    const message = await response.text();
    throw new Error(
      `Failed to fetch coordinator CoCo details (${response.status}): ${message}`,
    );
  }

  return response.json();
};

export const fetchCoordinatorClientDetails = async (cocoId, matterId, token) => {
  const response = await fetchWithRetry(
    `${API_BASE_URL}${COORDINATOR_CLIENT_DETAILS_PATH}/${encodeURIComponent(cocoId)}/clients/${encodeURIComponent(matterId)}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    const message = await response.text();
    throw new Error(
      `Failed to fetch coordinator client details (${response.status}): ${message}`,
    );
  }

  return response.json();
};

const handle403Error = () => {
  console.warn(`[403 Forbidden] Token may have expired`);
};

export const fetchSalesforceFiles = async (relatedToId, idToken, { grouped = false } = {}) => {
  const url = `${API_BASE_URL}/files?relatedToId=${relatedToId}${grouped ? "&grouped=true" : ""}`;

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (response.status === 403) {
    handle403Error();
    return { files: [] };
  }

  // A grouped request that fails falls back to the flat list instead of the error page.
  if (response.status === 500 && !grouped) {
    window.location.href = "/error";
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch files for ID ${relatedToId}`);
  }

  const data = await response.json();
  return data;
};

export const fetchCoordinatorSalesforceFiles = async (cocoId, matterId, idToken, { grouped = false } = {}) => {
  const url = `${API_BASE_URL}/cocos/${encodeURIComponent(cocoId)}`
    + `/clients/${encodeURIComponent(matterId)}/files${grouped ? "?grouped=true" : ""}`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch coordinator files for matter ${matterId}`);
  }

  return response.json();
};

export const fetchDownloadUrl = async (fileId, idToken) => {
  const url = `${API_BASE_URL}/files/${fileId}/download`;

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch download URL for fileId ${fileId}`);
  }

  const data = await response.json();
  return data.signedUrl;
};

export const downloadFileFromSignedUrl = async (signedUrl, fileName) => {
  const response = await fetch(signedUrl);

  if (!response.ok) {
    throw new Error(`Failed to download file ${fileName}`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = objectUrl;
  link.download = fileName;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};

export const getUploadSignedUrl = async (token, fileData) => {
  if (!token) throw new Error("No access token provided");

  try {
    const response = await fetch(`${API_BASE_URL}/files`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(fileData),
    });

    if (!response.ok) {
      const error = new Error(`HTTP error! status: ${response.status}`);
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error during POST request:", error);
    throw error;
  }
};

export const uploadFileToSignedUrl = async (file, signedUrl) => {
  try {
    const response = await fetch(signedUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type,
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Upload failed with status: ${response.status}`);
    }
    return response;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

export const postFileUploadComplete = async (token, fileId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/files/${fileId}/complete`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        status: "SUCCESS",
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const text = typeof response.text === "function" ? await response.text() : null;
    if (text === null) return response.json();
    return text ? JSON.parse(text) : {};
  } catch (error) {
    console.error("Error during POST request:", error);
    throw error;
  }
};

// `redirectOnServerError: false` lets secondary callers (e.g. the matter page) handle a 500 themselves.
export const fetchClientPartyDetails = async (token, partyId, signal, { redirectOnServerError = true } = {}) => {
  const url = `${API_BASE_URL}/clients/parties/${partyId}`;

  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    signal,
  });

  if (response.status === 403) {
    handle403Error();
    return null;
  }

  if (response.status === 500 && redirectOnServerError) {
    window.location.href = "/error";
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch party details for ID ${partyId}`);
  }

  const data = await response.json();
  return data;
};

export const fetchClientMatterDetails = async (token, matterId, signal) => {
  const url = `${API_BASE_URL}/clients/matters/${matterId}`;

  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    signal,
  });

  if (response.status === 403) {
    handle403Error();
    return null;
  }

  if (response.status === 500) {
    window.location.href = "/error";
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch matter details for ID ${matterId}`);
  }

  const data = await response.json();
  return data;
};

const performClientLookup = async (payload, token, signal) => {
  const url = `${API_BASE_URL}/clients/lookup`;
  const options = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
    signal,
  };

  const response = await fetchWithRetry(url, options, 1); // 1 retry on 504

  if (response.status === 403) {
    handle403Error();
    return null;
  }

  if (response.status === 404) {
    return {
      found: false,
      code: "cpfSearch.noResults",
    };
  }

  if (response.status === 500) {
    throw new Error("Client lookup service failed");
  }

  if (!response.ok) {
    const err = await response.text();
    throw new Error(`HTTP error! Status: ${response.status} - ${err}`);
  }

  return response.json();
};

export const lookupClientDocument = async (
  documentType,
  value,
  token,
  signal,
) => {
  if (!isValidClientDocument(value, documentType)) {
    throw new TypeError(`Invalid ${documentType} value`);
  }

  return performClientLookup(
    {
      identifierType: documentType,
      identifier: formatClientDocument(value, documentType),
    },
    token,
    signal,
  );
};

export const lookupClientCpf = async (cpf, token, signal) => {
  const normalizedCpf = normalizeCpf(cpf);
  if (!isValidClientDocument(normalizedCpf, CLIENT_DOCUMENT_TYPES.CPF)) {
    throw new TypeError("Invalid CPF value");
  }

  return performClientLookup(
    { cpf: normalizedCpf },
    token,
    signal,
  );
};

// Coordinator-only global search on the same lookup endpoint. Unlike the
// document lookup, error codes are surfaced so the page can explain them.
const postClientLookup = async (payload, token, signal, label) => {
  const response = await fetchWithRetry(
    `${API_BASE_URL}/clients/lookup`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      signal,
    },
    1,
  );

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const error = new Error(`${label} failed (${response.status})`);
    error.status = response.status;
    error.code = body?.error || body?.code || body?.errorCode || null;
    throw error;
  }

  return response.json();
};

export const searchClientsGlobal = (query, searchType, token, signal, limit) =>
  postClientLookup(buildGlobalSearchPayload(query, searchType, limit), token, signal, "Global client search");

export const searchClientsAdvanced = (criteria, role, token, signal) =>
  postClientLookup(buildAdvancedSearchPayload(criteria, role), token, signal, "Advanced client search");

export const updateCoCoProfile = async (cocoId, currentEmail, newEmail, newPhone, token) => {
  if (isMaskedPhone(newPhone)) {
    throw new TypeError("Masked phone values cannot be submitted");
  }
  if (newPhone !== "" && !isValidPhone(newPhone)) {
    throw new TypeError("Invalid phone values cannot be submitted");
  }

  const url = `${API_BASE_URL}/profiles/${encodeURIComponent(cocoId || "me")}`;

  const changes = {};
  if (newEmail !== "") changes.email = newEmail;
  if (newPhone !== "") changes.phone = newPhone;

  const data = {
    email: currentEmail,
    changes,
  };

  try {
    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = new Error('Profile update failed');
      const errorBody = await response.json().catch(() => ({}));
      error.code = errorBody.error;
      throw error;
    }

    return { success: true, status: response.status };
  } catch (error) {
    console.error("Failed to update profile data:", error);
    throw error;
  }
};

export const updateClientPartyContact = async (partyId, changes, token) => {
  if (isMaskedPhone(changes?.phone)) {
    throw new TypeError("Masked phone values cannot be submitted");
  }
  if (
    Object.prototype.hasOwnProperty.call(changes || {}, "phone") &&
    !isValidPhone(changes.phone)
  ) {
    throw new TypeError("Invalid phone values cannot be submitted");
  }

  const response = await fetch(
    `${API_BASE_URL}/clients/parties/${encodeURIComponent(partyId)}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ changes }),
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Error ${response.status}: ${errorText || "Unknown error"}`);
  }

  return { success: true, status: response.status };
};

export class AddressUpdateError extends Error {
  constructor(message, { status, code, fieldErrors } = {}) {
    super(message || "");
    this.name = "AddressUpdateError";
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors && typeof fieldErrors === "object" ? fieldErrors : undefined;
  }
}

const ADDRESS_PATCH_MOCK_DELAY_MS = 600;

export const isAddressPatchMockEnabled = (env = process.env) =>
  env.NODE_ENV === "development" && env.REACT_APP_MOCK_ADDRESS_PATCH === "true";

// Dev-only stand-in for PATCH /clients/parties/{id}/address until the backend ships.
// House number "400" simulates a validation error, "403" a non-owner and "409" a Salesforce rejection.
export const mockUpdateClientPartyAddress = (address, currentDetails = {}, delayMs = ADDRESS_PATCH_MOCK_DELAY_MS) =>
  new Promise((resolve, reject) => {
    setTimeout(() => {
      if (address.houseNumber === "400") {
        reject(new AddressUpdateError("Número inválido (mock 400).", {
          status: 400,
          fieldErrors: { houseNumber: "Número inválido (mock 400)." },
        }));
        return;
      }
      if (address.houseNumber === "403") {
        reject(new AddressUpdateError(
          "Você não tem permissão para alterar o endereço desse cliente",
          { status: 403 },
        ));
        return;
      }
      if (address.houseNumber === "409") {
        reject(new AddressUpdateError(
          "Você não tem permissão para alterar o endereço desse cliente",
          { status: 409 },
        ));
        return;
      }
      resolve({ details: { ...currentDetails, ...address } });
    }, delayMs);
  });

const isMissingAddressRoute = (response, body) =>
  [403, 404, 405].includes(response.status) && typeof body?.error !== "string";

export const updateClientPartyAddress = async (partyId, address, token, { currentDetails } = {}) => {
  if (isAddressPatchMockEnabled()) {
    return mockUpdateClientPartyAddress(address, currentDetails);
  }

  const partyUrl = `${API_BASE_URL}/clients/parties/${encodeURIComponent(partyId)}`;
  const patch = async (url, payload) => {
    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    let body = null;
    try {
      const text = await response.text();
      body = text ? JSON.parse(text) : null;
    } catch (error) {
      body = null;
    }
    return { response, body };
  };

  let { response, body } = await patch(`${partyUrl}/address`, address);
  // Until the dedicated gateway route exists, API Gateway answers 403/404 without the backend's
  // `error` code; the party PATCH accepts the same address under `{ address }`.
  if (isMissingAddressRoute(response, body)) {
    ({ response, body } = await patch(partyUrl, { address }));
  }

  if (!response.ok) {
    throw new AddressUpdateError(typeof body?.message === "string" ? body.message : "", {
      status: response.status,
      code: typeof body?.error === "string" ? body.error : undefined,
      fieldErrors: body?.fieldErrors,
    });
  }

  if (body?.details) return body;
  return { details: { ...(currentDetails || {}), ...(body || address) } };
};

export class TaxIdChangeError extends Error {
  constructor(message, { status, code, fieldErrors } = {}) {
    super(message || "");
    this.name = "TaxIdChangeError";
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors && typeof fieldErrors === "object" ? fieldErrors : undefined;
  }
}

const TAX_ID_CHANGE_MOCK_DELAY_MS = 3000;

export const isTaxIdChangeMockEnabled = (env = process.env) =>
  env.NODE_ENV === "development" && env.REACT_APP_MOCK_TAX_ID_CHANGE === "true";

const addDaysIso = (days, from = new Date()) => {
  const date = new Date(from.getTime() + days * 24 * 60 * 60 * 1000);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

// Dev-only stand-in for POST /clients/parties/{id}/tax-id-change-request (and the uploads) for local work.
// The first 3 digits pick the outcome: 400/403/409/413/429/502 simulate that error, 111 returns APPLIED,
// anything else UNDER_REVIEW. Valid CPFs: 400.000.000-43, 403.000.000-22, 409.000.000-90,
// 413.000.000-40, 429.000.000-25, 502.000.000-04, 111.444.777-35.
export const mockRequestPartyTaxIdChange = (payload, delayMs = TAX_ID_CHANGE_MOCK_DELAY_MS) =>
  new Promise((resolve, reject) => {
    setTimeout(() => {
      const prefix = String(payload?.newValue || "").slice(0, 3);
      const errors = {
        400: ["VALIDATION_ERROR", "Mock 400", { newValue: "SAME_AS_CURRENT" }],
        403: ["FORBIDDEN", "Mock 403"],
        409: ["REQUEST_PENDING", "Mock 409"],
        413: ["PAYLOAD_TOO_LARGE", "Mock 413"],
        429: ["RATE_LIMITED", "Mock 429"],
        502: ["UPSTREAM_ERROR", "Mock 502"],
      };
      if (errors[prefix]) {
        const [code, message, fieldErrors] = errors[prefix];
        reject(new TaxIdChangeError(message, { status: Number(prefix), code, fieldErrors }));
        return;
      }
      resolve({
        status: prefix === "111" && payload?.type === "CPF" ? "APPLIED" : "UNDER_REVIEW",
        type: payload?.type,
        taskId: "00TMOCK000000000001",
        matterId: payload?.matterId || null,
        dueDate: addDaysIso(7),
      });
    }, delayMs);
  });

let mockTaxIdUploadCount = 0;

// Contract v2: each document goes through the regular matter upload first; the request then sends the ids.
export const uploadTaxIdDocument = async ({ token, file, matterId, author }) => {
  if (isTaxIdChangeMockEnabled()) {
    await new Promise((resolve) => { setTimeout(resolve, 800); });
    mockTaxIdUploadCount += 1;
    return `mock-file-${mockTaxIdUploadCount}`;
  }
  const result = await uploadMatterDocument({
    token,
    file: toUploadFile(file),
    matterId,
    author,
    category: TAX_ID_DOCUMENT_CATEGORY,
  });
  return result.fileID;
};

// Never retried automatically: a retry after a gateway timeout could create a second CL.CPF task.
export const requestPartyTaxIdChange = async (partyId, payload, token, { signal } = {}) => {
  if (isTaxIdChangeMockEnabled()) {
    return mockRequestPartyTaxIdChange(payload);
  }

  const response = await fetch(
    `${API_BASE_URL}/clients/parties/${encodeURIComponent(partyId)}/tax-id-change-request`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      signal,
    },
  );

  let body = null;
  try {
    const text = await response.text();
    body = text ? JSON.parse(text) : null;
  } catch (error) {
    body = null;
  }

  if (!response.ok) {
    throw new TaxIdChangeError(typeof body?.message === "string" ? body.message : "", {
      status: response.status,
      code: typeof body?.error === "string" ? body.error : undefined,
      fieldErrors: body?.fieldErrors,
    });
  }

  return body || {};
};

export const storeRefreshToken = async (refreshToken) => {
  const url = `${API_BASE_URL}/auth/store`;
  try {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      throw new Error(`Unable to store refresh token: ${response.status}`);
    }
  } catch (err) {
    console.error("Failed to store refresh token on backend", err);
    throw err;
  }
};

export const refreshAccessTokenApi = async () => {
  const url = `${API_BASE_URL}/auth/refresh`;

  try {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
    });

    // Handle expected status codes explicitly
    if (response.status === 401) {
      // Not authorized — expected in some cases (e.g., expired session)
      return null;
    }

    if (!response.ok) {
      // Only throw for unexpected non-401 errors
      throw new Error(`Unexpected status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (err) {
    // Suppress known/expected errors (e.g., network offline, CORS failure)
    if (err instanceof TypeError && err.message.includes("Failed to fetch")) {
      // Network-related error — optional to log
      console.warn("Network error during token refresh:", err.message);
    } else {
      console.error("Error refreshing access token:", err);
    }
    return null;
  }
};

export const clearRefreshToken = async () => {
  const url = `${API_BASE_URL}/auth/clear`;
  try {
    await fetch(url, {
      method: "POST",
      credentials: "include", // Send the cookie so backend can clear it
    });
  } catch (err) {
    // throw err;
  }
};

// Legacy temp-password recovery, used only by SignInLegacy (sign-in modes disabled, e.g. PROD).
export const requestPasswordRecovery = async (email) => {
  const response = await fetch(`${API_BASE_URL}/auth/password-recovery/request`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    throw new Error("Failed to request password recovery");
  }

  return response.json();
};

export const verifyPasswordRecoveryOtp = async (transactionId, otp) => {
  const response = await fetch(`${API_BASE_URL}/auth/password-recovery/verify`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ transactionId, otp }),
  });

  if (!response.ok) {
    throw new Error("Failed to verify password recovery code");
  }

  return response.json();
};

export const issuePasswordRecoveryTemporaryPassword = async (recoveryToken) => {
  const response = await fetch(`${API_BASE_URL}/auth/password-recovery/issue-temporary-password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ recoveryToken }),
  });

  if (!response.ok) {
    throw new Error("Failed to issue temporary password");
  }

  return response.json();
};

export class PasswordSettingsError extends Error {
  constructor(message, { status, code } = {}) {
    super(message);
    this.name = "PasswordSettingsError";
    this.status = status;
    this.code = code;
  }
}

// /users/me/password* and /users/me/sign-in-method all take the ID token.
const userSettingsRequest = async (path, idToken, { method = "GET", body, headers = {} } = {}) => {
  const response = await fetch(`${API_BASE_URL}/users/me${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
      ...headers,
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new PasswordSettingsError(
      `User settings request failed (${response.status})`,
      {
        status: response.status,
        code: typeof errorBody?.error === "string" ? errorBody.error : undefined,
      },
    );
  }

  return response.status === 204 ? null : response.json().catch(() => null);
};

// { hasPassword, promptSkipped } for the signed-in user.
export const fetchPasswordStatus = async (idToken) => {
  const body = await userSettingsRequest("/password", idToken);
  return {
    hasPassword: Boolean(body?.hasPassword),
    promptSkipped: Boolean(body?.promptSkipped),
  };
};

// Sets a permanent password (fresh session required). `method` is PASSWORD_AND_CODE
// (also the backend default).
export const createPassword = async (password, idToken, method) => {
  await userSettingsRequest("/password", idToken, {
    method: "POST",
    body: method ? { password, method } : { password },
  });
};

// Records that the user chose to keep signing in with an email code only.
export const skipPasswordPrompt = async (idToken) => {
  await userSettingsRequest("/password/skip", idToken, { method: "POST" });
};

// GET/PUT /sign-in-method and POST /passkeys/sync share this body. `method` may be the
// legacy PASSWORD_ONLY (read-only). passkeyRequiresCodeOnly: only CODE_ONLY is allowed.
const toSignInMethodStatus = (body) => ({
  method: body?.method || null,
  hasPassword: Boolean(body?.hasPassword),
  hasPasskey: Boolean(body?.hasPasskey),
  passkeyRequiresCodeOnly: Boolean(body?.passkeyRequiresCodeOnly),
});

export const fetchSignInMethod = async (idToken) =>
  toSignInMethodStatus(await userSettingsRequest("/sign-in-method", idToken));

// Returns the new status, or null if the body was empty.
// 409 PASSWORD_REQUIRED / PASSKEY_ACTIVE; 401 REAUTH_REQUIRED.
export const updateSignInMethod = async (method, idToken) => {
  const body = await userSettingsRequest("/sign-in-method", idToken, { method: "PUT", body: { method } });
  return body ? toSignInMethodStatus(body) : null;
};

// Reconciles the account with its Cognito passkeys: with any passkey the backend forces
// CODE_ONLY (no usable password); with none, password + code gets its email MFA back.
// 401 INVALID_ACCESS_TOKEN when the access token is invalid or belongs to someone else.
export const syncPasskeys = async (idToken, accessToken) =>
  toSignInMethodStatus(
    await userSettingsRequest("/passkeys/sync", idToken, {
      method: "POST",
      headers: { "X-Cognito-Access-Token": accessToken },
    }),
  );

// The support form lives behind /help, which is a ProtectedRoute, so the caller
// always has a session. The token is now required: the migrated gateway puts
// this route behind the Cognito authorizer (TDDS-3179) instead of leaving an
// authenticated-only feature on an unauthenticated endpoint.
export class ContactRequestError extends Error {
  constructor(status, fieldErrors = {}) {
    super(`Contact request failed (${status})`);
    this.name = "ContactRequestError";
    this.status = status;
    this.fieldErrors = fieldErrors;
  }
}

// Body is only { enquiryType, message }; the backend takes the requester from the ID
// token and sends to its configured support inbox.
// 400 { error: "INVALID_CONTACT_REQUEST", message, fieldErrors: { field: message } }, 502 EMAIL_SEND_FAILED.
export const sendContactRequest = async ({ enquiryType, message }, token) => {
  const response = await fetch(`${API_BASE_URL}/contact-requests`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ enquiryType, message }),
  });

  if (response.status === 403) {
    handle403Error();
    throw new ContactRequestError(403);
  }

  if (!response.ok) {
    let fieldErrors = {};
    if (response.status === 400) {
      const body = await response.json().catch(() => null);
      if (body?.fieldErrors && typeof body.fieldErrors === "object") fieldErrors = body.fieldErrors;
    }
    throw new ContactRequestError(response.status, fieldErrors);
  }

  return response.status;
};

// Legacy body used by SupportFormLegacy (helpSupport off); the backend still accepts it.
export const sendEmail = async (data, token) => {
  const response = await fetch(`${API_BASE_URL}/contact-requests`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    // Any non-2xx (including WAF/API Gateway 403s) means the request was not delivered.
    if (response.status === 403) handle403Error();
    throw new ContactRequestError(response.status);
  }

  return response.status;
};

// Maps the portal UI language (see TranslationContext) to a BCP-47 tag.
const UI_LANGUAGE_TO_LOCALE = { pt: "pt-BR", en: "en" };

export const getUiLocale = (language) => {
  let lang = language;

  if (lang === undefined) {
    try {
      lang = typeof localStorage !== "undefined" ? localStorage.getItem("appLanguage") : null;
    } catch {
      lang = null;
    }
  }

  return UI_LANGUAGE_TO_LOCALE[lang];
};

export const sendSignInHelpForm = async ({
  email,
  issueType,
  detail,
  submittedAt,
  language,
  oabNumber,
  oabState,
}) => {
  const locale = getUiLocale(language);
  // Only the sign-in-modes form sends OAB fields; PROD's gateway model doesn't accept them yet.
  const includeOab = oabNumber !== undefined || oabState !== undefined;

  const response = await fetch(`${API_BASE_URL}/users/login-issues`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email,
      issueType,
      detail,
      submittedAt,
      ...(includeOab ? { oabNumber: oabNumber || null, oabState: oabState || null } : {}),
      ...(locale ? { locale } : {}),
    }),
  });

  if (!response.ok) {
    let errorMessage = `Error submitting sign-in help form: ${response.statusText}`;

    try {
      const errorBody = await response.clone().json();
      if (errorBody?.error) {
        errorMessage = errorBody.error;
      }
    } catch {
      // ignore JSON parse errors
    }

    const error = new Error(errorMessage);
    error.status = response.status;
    throw error;
  }

  // ✅ Explicit success on 200 / 201
  if (response.status === 200 || response.status === 201) {
    return { success: true };
  }

  return { success: false };
};

// Asks for the grouped documents shape (TDDS-3384); if the backend doesn't support it yet
// (error or a flat `{ files }` body), the caller simply gets the flat response back.
export const fetchFilesPreferGrouped = async (fetchFlat, ...args) => {
  try {
    return await fetchFlat(...args, { grouped: true });
  } catch (error) {
    console.warn("Grouped files request failed, falling back to the flat list", error);
    return fetchFlat(...args);
  }
};

export const updateFileCategory = async (fileId, category, token) => {
  const response = await fetch(`${API_BASE_URL}/files/${encodeURIComponent(fileId)}/category`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ category: category || null }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const error = new Error(`Failed to update category (${response.status})`);
    error.status = response.status;
    error.code = body?.error || null;
    throw error;
  }
  return response.json();
};

// Upload flow (init → signed URL PUT → complete) with an optional document category.
export const uploadMatterDocument = async ({ token, file, matterId, author, category }) => {
  const fileData = {
    name: file.name,
    description: "",
    author,
    folderPath: [],
    tags: [],
    relatedObjectId: matterId,
    relatedObjectName: "litify_pm__Matter__c",
    requestedAt: new Date().toISOString(),
    ...(category ? { category } : {}),
  };
  const response = await getUploadSignedUrl(token, fileData);
  await uploadFileToSignedUrl(file, response.signedUrl);
  // Any 2xx from /complete means the upload is done; TEST answers with an empty `{}` body.
  const result = await postFileUploadComplete(token, response.fileID);
  return { ...(result || {}), success: true, fileID: result?.fileID || response.fileID };
};

export const PORTAL_EVENT_TYPES = Object.freeze({
  LOGIN_SUCCESS: "LOGIN_SUCCESS",
  LOGIN_FAILED: "LOGIN_FAILED",
  OTP_SENT: "OTP_SENT",
  OTP_RESENT: "OTP_RESENT",
  OTP_SUBMITTED: "OTP_SUBMITTED",
  NEW_PASSWORD_REQUIRED: "NEW_PASSWORD_REQUIRED",
  NEW_PASSWORD_SET: "NEW_PASSWORD_SET",
  PASSWORD_RESET_REQUESTED: "PASSWORD_RESET_REQUESTED",
  PASSWORD_RESET_CODE_SENT: "PASSWORD_RESET_CODE_SENT",
  PASSWORD_RESET_COMPLETED: "PASSWORD_RESET_COMPLETED",
  LOGOUT: "LOGOUT",
});

const PORTAL_EVENT_TIMEOUT_MS = 5000;
const PORTAL_EVENT_MAX_MESSAGE_LENGTH = 200;
// Defence in depth: credentials must never leave the browser in an analytics event.
const PORTAL_EVENT_FORBIDDEN_METADATA_KEYS = /pass(word)?|otp|code$|secret|token/i;

export const generateLoginAttemptId = () => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // Fall through to the non-cryptographic fallback.
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const sanitiseEventMetadata = (metadata) => {
  if (!metadata || typeof metadata !== "object") return null;
  const clean = {};
  Object.entries(metadata).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (key !== "errorCode" && PORTAL_EVENT_FORBIDDEN_METADATA_KEYS.test(key)) return;
    clean[key] =
      typeof value === "string" && value.length > PORTAL_EVENT_MAX_MESSAGE_LENGTH
        ? value.slice(0, PORTAL_EVENT_MAX_MESSAGE_LENGTH)
        : value;
  });
  return Object.keys(clean).length > 0 ? clean : null;
};

// Fire-and-forget login funnel telemetry. Never throws, never needs awaiting,
// and never delays the UI. Do NOT pass passwords or OTP codes.
export const sendPortalEvent = (
  eventType,
  { loginAttemptId, email, metadata, idToken } = {},
) => {
  try {
    if (!eventType || typeof fetch !== "function") return Promise.resolve(false);

    const cleanMetadata = sanitiseEventMetadata({
      appVersion: process.env.REACT_APP_VERSION,
      ...metadata,
    });
    const body = {
      eventType,
      ...(loginAttemptId ? { loginAttemptId } : {}),
      ...(!idToken && email ? { email: String(email).trim().toLowerCase() } : {}),
      ...(cleanMetadata ? { metadata: cleanMetadata } : {}),
    };

    const controller =
      typeof AbortController !== "undefined" ? new AbortController() : null;
    const timeoutId = controller
      ? setTimeout(() => controller.abort(), PORTAL_EVENT_TIMEOUT_MS)
      : null;

    return fetch(`${API_BASE_URL}/events`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body: JSON.stringify(body),
      ...(controller ? { signal: controller.signal } : {}),
    })
      .then((response) => Boolean(response?.ok))
      .catch(() => false)
      .finally(() => {
        if (timeoutId) clearTimeout(timeoutId);
      });
  } catch {
    return Promise.resolve(false);
  }
};
